import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { motion } from 'framer-motion';
import { Plus, TrendingUp, Users, AlertTriangle, CheckCircle, Search, MapPin } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useMarkers } from '../hooks/useMarkers';
import { useReports } from '../hooks/useReports';
import ReportModal from '../components/ReportModal/ReportModal';
import { HazardReport } from '../types';
import styles from '../styles/Dashboard.module.scss';

// Lazy load the map component for better performance
const LeafletMap = lazy(() => import('../components/Map/LeafletMap'));

interface DashboardStats {
  totalMarkers: number;
  activeReports: number;
  resolvedIssues: number;
  userContributions: number;
}

// Simple loading skeleton component
const LoadingSkeleton: React.FC = () => (
  <div className={styles.skeleton}>
    <div className={styles.skeletonLine}></div>
    <div className={styles.skeletonLine}></div>
    <div className={styles.skeletonLine}></div>
  </div>
);

// Simple stats card component
const StatsCard: React.FC<{
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
}> = ({ title, value, icon, color }) => (
  <motion.div 
    className={styles.statsCard}
    whileHover={{ scale: 1.02 }}
    transition={{ duration: 0.2 }}
  >
    <div className={styles.cardHeader}>
      <div className={`${styles.iconContainer} ${styles[color]}`}>
        {icon}
      </div>
    </div>
    <div className={styles.cardBody}>
      <div className={styles.value}>
        <span>{value}</span>
      </div>
      <h3 className={styles.title}>{title}</h3>
    </div>
  </motion.div>
);

// Simple search bar component
const SearchBar: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}> = ({ value, onChange, placeholder }) => (
  <div className={styles.searchContainer}>
    <Search className={styles.searchIcon} size={20} />
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={styles.searchInput}
    />
  </div>
);

// Floating action button component
const FloatingActionButton: React.FC<{
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}> = ({ onClick, icon, label }) => (
  <motion.button
    className={styles.fab}
    onClick={onClick}
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.95 }}
    title={label}
  >
    {icon}
  </motion.button>
);

const Dashboard: React.FC = () => {
  const { currentUser } = useAuth();
  const { markers, loading: markersLoading } = useMarkers();
  const { reports, loading: reportsLoading, getReportsStats } = useReports();
  
  // Get real-time stats from reports
  const stats = getReportsStats();

  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [isMapCollapsed, setIsMapCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  // Get real-time stats from reports
  const dashboardStats = getReportsStats();

  // Function to refresh dashboard data after report submission
  const refreshDashboard = useCallback(async () => {
    // Reports will automatically update via real-time listener in useReports hook
    // No manual refresh needed
  }, []);

  // Handle successful report submission
  const handleReportSuccess = useCallback(() => {
    // Reports will automatically update via real-time listener
    setIsReportModalOpen(false);
  }, []);

  // No need for manual data fetching - useReports hook handles real-time data

  // Use real reports from useReports hook instead of mock data
  const displayReports = reports.length > 0 ? reports : [];

  const filterOptions = [
    { value: 'all', label: 'All Reports', icon: '📊' },
    { value: 'tsunami', label: 'Tsunami', icon: '🌊' },
    { value: 'high-waves', label: 'High Waves', icon: '🌊' },
    { value: 'coastal-flooding', label: 'Flooding', icon: '💧' },
    { value: 'storm-surge', label: 'Storm Surge', icon: '⛈️' },
    { value: 'rip-current', label: 'Rip Current', icon: '🌀' }
  ];

  // Filter reports based on search query and active filter
  const filteredReports = displayReports.filter((report: HazardReport) => {
    const matchesSearch = report.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      report.hazardType.toLowerCase().includes(searchQuery.toLowerCase()) ||
      report.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = activeFilter === 'all' || report.hazardType === activeFilter;
    return matchesSearch && matchesFilter;
  });

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'low': return '#00ff88';
      case 'medium': return '#ffaa00';
      case 'high': return '#ff4444';
      case 'critical': return '#cc0000';
      default: return '#ffffff';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return '#ffaa00';
      case 'verified': return '#00ff88';
      case 'resolved': return '#888888';
      default: return '#ffffff';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'tsunami': return '🌊';
      case 'high-waves': return '🌊';
      case 'coastal-flooding': return '💧';
      case 'storm-surge': return '⛈️';
      case 'rip-current': return '🌀';
      case 'marine-debris': return '🗑️';
      default: return '⚠️';
    }
  };

  // Calculate dashboard statistics - commented out to use static dummy data
  // useEffect(() => {
  //   if (markers.length > 0) {
  //     const userMarkers = markers.filter(m => m.userId === currentUser?.uid);
  //     setDashboardStats({
  //       totalMarkers: markers.length,
  //       activeReports: markers.filter(m => m.severity === 'high' || m.severity === 'critical').length,
  //       resolvedIssues: markers.filter(m => m.severity === 'low').length,
  //       userContributions: userMarkers.length
  //     });
  //   }
  // }, [markers, currentUser]);

  // Handle search and filter changes (placeholder for future implementation)
  const handleSearchChange = (query: string, newFilters: any) => {
    // setSearchQuery(query); // Commented out - unused for now
    // setFilters(newFilters); // Commented out - unused for now
    // Search logic can be implemented here if needed
  };

  // Handle filter button clicks
  // const handleFilterClick = (filter: string) => { // Unused for now
  //   setActiveFilter(filter);
  // };

  if (markersLoading) {
    return (
      <div className={styles.dashboard}>
        <div className={styles.container}>
          <LoadingSkeleton />
          <LoadingSkeleton />
          <LoadingSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.dashboardPage}>
      <div className={styles.container}>
        {/* Header */}
        <motion.div 
          className={styles.header}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className={styles.title}>Ocean Guardian Dashboard</h1>
          <p className={styles.subtitle}>
            Monitor ocean health and track hazard reports in real-time
          </p>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          className={styles.statsGrid}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <StatsCard
            title="Total Reports"
            value={stats.totalReports}
            icon={<TrendingUp size={24} />}
            color="cyan"
          />
          <StatsCard
            title="Active Reports"
            value={stats.activeReports}
            icon={<AlertTriangle size={24} />}
            color="orange"
          />
          <StatsCard
            title="Resolved Issues"
            value={stats.resolvedReports}
            icon={<CheckCircle size={24} />}
            color="green"
          />
          <StatsCard
            title="Your Contributions"
            value={stats.userReports}
            icon={<Users size={24} />}          
            color="purple"
          />
        </motion.div>

        {/* Search Bar */}
        <motion.div 
          className={styles.searchSection}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <SearchBar
            value={searchQuery}
            onChange={(value) => handleSearchChange(value, {})}
            placeholder="Search markers, locations, or reports..."
          />
        </motion.div>

        {/* Interactive Map Section */}
        <motion.div 
          className={`${styles.mapSection} ${isMapCollapsed ? styles.collapsed : ''}`}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <div className={styles.mapHeader}>
            <h2 className={styles.mapTitle}>Live Ocean Monitoring</h2>
            <button 
              className={styles.collapseButton}
              onClick={() => setIsMapCollapsed(!isMapCollapsed)}
            >
              {isMapCollapsed ? '📈' : '📉'}
            </button>
          </div>
          
          {!isMapCollapsed && (
            <div className={styles.mapContainer}>
              <Suspense fallback={
                <div className={styles.mapLoading}>
                  <motion.div 
                    className={styles.loadingSpinner}
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  >
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 12a9 9 0 11-6.219-8.56"/>
                    </svg>
                  </motion.div>
                  <p>Loading dashboard map...</p>
                </div>
              }>
                <LeafletMap 
                  reports={reports} // Pass real-time reports to the map
                  onLocationSelect={() => {}}
                  selectedLocation={null}
                  className={styles.dashboardMap}
                />
              </Suspense>
            </div>
          )}
        </motion.div>

        {/* Filter Buttons */}
        <motion.div
          className={styles.filtersSection}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <h2 className={styles.sectionTitle}>Filter Reports</h2>
          <div className={styles.filterButtons}>
            {filterOptions.map((filter, index) => (
              <motion.button
                key={filter.value}
                className={`${styles.filterButton} ${activeFilter === filter.value ? styles.active : ''}`}
                onClick={() => setActiveFilter(filter.value)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <span className={styles.filterIcon}>{filter.icon}</span>
                <span className={styles.filterLabel}>{filter.label}</span>
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Reports Grid */}
        <motion.div
          className={styles.reportsSection}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.0 }}
        >
          <div className={styles.reportsGrid}>
            {filteredReports.map((report, index) => (
              <motion.div
                key={report.id}
                className={styles.reportCard}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                whileHover={{ 
                  y: -5,
                  scale: 1.02,
                  transition: { duration: 0.3 }
                }}
              >
                <div className={styles.reportHeader}>
                  <div className={styles.reportType}>
                    <span className={styles.typeIcon}>{getTypeIcon(report.hazardType)}</span>
                    <span className={styles.typeName}>{report.hazardType.replace('-', ' ')}</span>
                  </div>
                  <div 
                    className={styles.severityBadge}
                    style={{ backgroundColor: getSeverityColor(report.severity) }}
                  >
                    {report.severity}
                  </div>
                </div>

                <h3 className={styles.reportTitle}>{report.title}</h3>
                
                <div className={styles.reportDetails}>
                  <div className={styles.reportLocation}>
                    <span className={styles.locationIcon}>📍</span>
                    <span>{report.latitude.toFixed(4)}, {report.longitude.toFixed(4)}</span>
                  </div>
                  <div className={styles.reportTime}>
                    <span className={styles.timeIcon}>🕐</span>
                    <span>{report.createdAt.toLocaleDateString()}</span>
                  </div>
                </div>

                <div className={styles.reportFooter}>
                  <div 
                    className={styles.statusBadge}
                    style={{ backgroundColor: getStatusColor(report.status) }}
                  >
                    {report.status}
                  </div>
                  <button className={styles.viewButton}>
                    View Details
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M7 17L17 7"/>
                      <path d="M7 7h10v10"/>
                    </svg>
                  </button>
                </div>

                <div className={styles.cardGlow}></div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Floating Action Button */}
      <FloatingActionButton 
        onClick={() => setIsReportModalOpen(true)} 
        icon={<Plus size={24} />}
        label="Report Hazard"
      />

      {/* Report Modal */}
      <ReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        onSuccess={handleReportSuccess}
      />
    </div>
  );
};

export default Dashboard;
